// frontend/src/pages/Driver.jsx
import React, { useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import "./Driver.css";
import api from "../utils/api";   // top of the file

/* ---------------- Validation Schema ---------------- */
const schema = yup.object().shape({
  first_name: yup.string().min(2, "First name is required").required(),
  last_name: yup.string().min(2, "Last name is required").required(),
  phone: yup
    .string()
    .matches(/^(\+44\d{9,10}|0\d{9,10})$/, "Enter a valid UK phone number")
    .required("Phone is required"),
  dob: yup
    .date()
    .max(new Date(Date.now() - 568025136000), "You must be 18+") // ~18 years
    .required("Date of birth is required"),
  address_line1: yup.string().required("Address line 1 is required"),
  city: yup.string().required("City is required"),
  postcode: yup
    .string()
    .matches(
      /^([A-Z]{1,2}\d[A-Z\d]?)\s?(\d[ABD-HJLNP-UW-Z]{2})$/i,
      "Enter a valid UK postcode"
    )
    .required("Postcode is required"),
  transport: yup
    .string()
    .oneOf(["bicycle", "motorcycle", "car"], "Select your transport")
    .required("Transport is required"),
  work_status: yup
    .string()
    .oneOf(
      ["uk_citizen", "settled_status", "work_visa", "student_visa", "other"],
      "Select work eligibility"
    )
    .required(),
  dbs_status: yup
    .string()
    .oneOf(["clear", "pending", "not_provided"], "Select DBS status")
    .required(),
  password: yup.string().min(8, "Password must be at least 8 characters").required(),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Please confirm your password"),
  gdpr_consent: yup.bool().oneOf([true], "Consent is required"),
  terms_accepted: yup.bool().oneOf([true], "You must accept the terms"),
});

export default function Driver() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [verifyToken, setVerifyToken] = useState(null);
  const [msg, setMsg] = useState("");
  const [step, setStep] = useState("email");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  /* ---------------- Send code ---------------- */
  const sendCode = async () => {
    setMsg("");
    try {
      const res = await api.post("/public/driver/send-code", { email });

      if (res.data.ok) {
        setStep("code");
        setMsg("✅ Code sent! Please check your email.");
      }
    } catch (err) {
      setMsg(err.response?.data?.error || "❌ Failed to send code");
    }
  };

  /* ---------------- Verify code ---------------- */
  const verifyCode = async () => {
    setMsg("");
    try {
      const res = await api.post("/public/driver/verify-code", { email, code });

      if (res.data.ok) {
        setVerifyToken(res.data.verifyToken);
        setStep("form");
        setMsg("✅ Email verified. Please complete your application.");
      }
    } catch (err) {
      setMsg(err.response?.data?.error || "❌ Invalid or expired code");
    }
  };

  /* ---------------- Submit application ---------------- */
  const onSubmit = async (data) => {
    if (!verifyToken) {
      setMsg("❌ Please verify your email first");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(data).forEach(([k, v]) => {
        if (v && !(v instanceof FileList)) formData.append(k, v);
      });
      ["profile_photo", "right_to_work_doc", "dbs_doc", "insurance_doc", "licence_doc"].forEach(
        (f) => {
          if (data[f]?.[0]) formData.append(f, data[f][0]);
        }
      );
      formData.append("verifyToken", verifyToken);
      formData.append("email", email);

      const res = await api.post("/public/driver/apply", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.ok) {
        // ✅ Auto-login immediately
        localStorage.setItem("driver_token", res.data.token);
        localStorage.setItem("driver", JSON.stringify(res.data.driver));

        navigate("/driver/dashboard");
      }
    } catch (err) {
      if (err.response?.data?.errors) {
        err.response.data.errors.forEach((e) =>
          setError(e.param, { type: "server", message: e.msg })
        );
      } else {
        setMsg(err.response?.data?.error || "❌ Failed to submit application");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="driver-wrapper">
      <div className="driver-card">
        <h1 className="text-2xl font-bold mb-2">Apply as a Delivery Driver</h1>

        {/* Step 1: Email */}
        {step === "email" && (
          <>
            <h2 className="text-xl font-medium mb-3">Step 1: Email Verification</h2>
            <p className="text-gray-600 mb-4">
              Verified, compliant drivers help us deliver end-to-end service for our sellers and customers.
            </p>
            <div className="flex flex-col md:flex-row gap-2 mb-4">
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 border rounded-lg p-3 text-lg"
              />
              <button
                type="button"
                onClick={sendCode}
                disabled={!email}
                className="bg-black text-white rounded-lg px-6 py-3 text-lg"
              >
                Send Code
              </button>
            </div>


{/* 🔑 Extra link for returning drivers */}
	
<p className="text-gray-600 mb-4">
  Already registered?{" "}
  <a
    href="/driver/login"
    className="text-green-700 font-semibold hover:underline"
  >
    Login here
  </a>
</p>

          </>
        )}

        {/* Step 2: Code */}
        {step === "code" && (
          <>
            <h2 className="text-xl font-medium mb-3">Step 2: Enter Verification Code</h2>
            <div className="flex flex-col md:flex-row gap-2 mb-4">
              <input
                type="text"
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="flex-1 border rounded-lg p-3 text-lg"
                maxLength={6}
              />
              <button
                type="button"
                onClick={verifyCode}
                disabled={code.length !== 6}
                className="bg-green-600 text-white rounded-lg px-6 py-3 text-lg"
              >
                Verify
              </button>
            </div>
          </>
        )}

        {/* Step 3: Form */}
        {step === "form" && (
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <h2 className="text-xl font-medium mb-3">Step 3: Your Details</h2>
            <div className="grid gap-4 md:grid-cols-2 mb-4">
              {/* Name */}
              <div>
                <input {...register("first_name")} placeholder="First name" className="border rounded-lg p-3 w-full text-lg" />
                {errors.first_name && <p className="text-red-600 text-sm">{errors.first_name.message}</p>}
              </div>
              <div>
                <input {...register("last_name")} placeholder="Last name" className="border rounded-lg p-3 w-full text-lg" />
                {errors.last_name && <p className="text-red-600 text-sm">{errors.last_name.message}</p>}
              </div>

              {/* Phone + DOB */}
              <div>
                <input {...register("phone")} placeholder="07..." className="border rounded-lg p-3 w-full text-lg" />
                <p className="text-gray-500 text-sm mt-1">📞 Mobile or landline, include +44 or 0</p>
                {errors.phone && <p className="text-red-600 text-sm">{errors.phone.message}</p>}
              </div>
              <div>
                <input type="date" {...register("dob")} className="border rounded-lg p-3 w-full text-lg" />
                <p className="text-gray-500 text-sm mt-1">🎂 Must be 18 years or older</p>
                {errors.dob && <p className="text-red-600 text-sm">{errors.dob.message}</p>}
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <input {...register("address_line1")} placeholder="Address line 1" className="border rounded-lg p-3 w-full text-lg" />
                {errors.address_line1 && <p className="text-red-600 text-sm">{errors.address_line1.message}</p>}
              </div>
              <input {...register("address_line2")} placeholder="Address line 2 (optional)" className="border rounded-lg p-3 w-full text-lg md:col-span-2" />
              <div>
                <input {...register("city")} placeholder="City" className="border rounded-lg p-3 w-full text-lg" />
                {errors.city && <p className="text-red-600 text-sm">{errors.city.message}</p>}
              </div>
              <div>
                <input {...register("postcode")} placeholder="Postcode" className="border rounded-lg p-3 w-full text-lg" />
                {errors.postcode && <p className="text-red-600 text-sm">{errors.postcode.message}</p>}
              </div>
            </div>

            {/* Work Eligibility */}
            <h2 className="text-xl font-medium mb-3">Step 4: Work Eligibility</h2>
            <select {...register("work_status")} className="w-full border rounded-lg p-3 text-lg mb-2">
              <option value="">Select status</option>
              <option value="uk_citizen">UK Citizen</option>
              <option value="settled_status">Settled Status</option>
              <option value="work_visa">Work Visa</option>
              <option value="student_visa">Student Visa</option>
              <option value="other">Other</option>
            </select>
            {errors.work_status && <p className="text-red-600 text-sm">{errors.work_status.message}</p>}

            {/* Transport */}
            <h2 className="text-xl font-medium mb-3">Step 5: Transport & Vehicle</h2>
            <select {...register("transport")} className="w-full border rounded-lg p-3 text-lg mb-2">
              <option value="">Select transport</option>
              <option value="bicycle">Bicycle</option>
              <option value="motorcycle">Motorcycle</option>
              <option value="car">Car</option>
            </select>
            {errors.transport && <p className="text-red-600 text-sm">{errors.transport.message}</p>}

            {/* Compliance */}
            <h2 className="text-xl font-medium mb-3">Step 6: Compliance</h2>
            <select {...register("dbs_status")} className="w-full border rounded-lg p-3 text-lg mb-2">
              <option value="">DBS status</option>
              <option value="clear">Clear</option>
              <option value="pending">Pending</option>
              <option value="not_provided">Not provided</option>
            </select>
            {errors.dbs_status && <p className="text-red-600 text-sm">{errors.dbs_status.message}</p>}

            {/* Password */}
            <h2 className="text-xl font-medium mb-3">Step 7: Account Security</h2>
            <div className="grid gap-4 md:grid-cols-2 mb-4">
              <div>
                <input type="password" {...register("password")} placeholder="Password" className="border rounded-lg p-3 w-full text-lg" />
                {errors.password && <p className="text-red-600 text-sm">{errors.password.message}</p>}
              </div>
              <div>
                <input type="password" {...register("confirmPassword")} placeholder="Confirm password" className="border rounded-lg p-3 w-full text-lg" />
                {errors.confirmPassword && <p className="text-red-600 text-sm">{errors.confirmPassword.message}</p>}
              </div>
            </div>

            {/* Consent */}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...register("gdpr_consent")} /> I consent to processing my data.
            </label>
            {errors.gdpr_consent && <p className="text-red-600 text-sm">{errors.gdpr_consent.message}</p>}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...register("terms_accepted")} /> I agree to the Terms & Conditions.
            </label>
            {errors.terms_accepted && <p className="text-red-600 text-sm">{errors.terms_accepted.message}</p>}

            {/* Upload */}
            <h2 className="text-xl font-medium mb-3 mt-4">Step 8: Upload Documents</h2>
            <div className="space-y-3">
              {[
                { key: "profile_photo", label: "Profile photo", hint: "Clear face photo (jpg, png, pdf)." },
                { key: "right_to_work_doc", label: "Right-to-Work document", hint: "Passport, BRP card, or visa (required)." },
                { key: "dbs_doc", label: "DBS certificate (optional)", hint: "Upload if you already hold a valid DBS." },
                { key: "insurance_doc", label: "Insurance document (optional)", hint: "Motorcycle/car insurance if applicable." },
                { key: "licence_doc", label: "Driving licence (optional)", hint: "Front and back of your licence." },
              ].map(({ key, label, hint }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input type="file" {...register(key)} className="block w-full text-sm text-gray-600 border rounded-lg cursor-pointer bg-white focus:outline-none" />
                  <p className="text-xs text-gray-500 mt-1">{hint}</p>
                </div>
              ))}
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`${
                loading ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700 active:bg-green-800"
              } text-white font-semibold rounded-xl shadow-md px-8 py-3 text-lg transition duration-200 ease-in-out w-full md:w-auto mt-4`}
            >
              {loading ? "Submitting..." : "Submit application"}
            </button>
          </form>
        )}

        {/* ✅ Removed old Step 4 confirmation — handled by redirect */}
        {msg && step !== "done" && <p className="text-sm text-gray-700 mt-4">{msg}</p>}
      </div>
    </div>
  );
}

